import axios from 'axios';
import { getStoredToken } from '../../login/api/loginApi';
import { attachAuthInterceptors } from '../../_shared/auth/authInterceptor';
import type { RestoreBaselineResult } from '../types/myProfile';

/**
 * configRestoreApi — POST /api/eraf/admin/config/restore-baseline
 * ROLE_ADMIN 전용 표준상태복구 API 클라이언트
 */
const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE ?? '/api/eraf',
  headers: { 'Content-Type': 'application/json' },
});
attachAuthInterceptors(apiClient);

// Request interceptor — Bearer 토큰 첨부
apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — ApiResponse<T> 언랩
apiClient.interceptors.response.use(
  (response) => {
    const d = response.data as Record<string, unknown> | undefined;
    if (d && typeof d === 'object' && 'success' in d) {
      if (d.success === true) {
        return { ...response, data: d['data'] };
      }
      const err = d['error'] as Record<string, unknown> | undefined;
      return Promise.reject({
        code: err?.['code'] ?? `HTTP-${response.status}`,
        message: err?.['message'] ?? 'Unknown error',
        traceId: (err?.['traceId'] as string) ?? response.headers?.['x-trace-id'],
        fieldErrors: err?.['fieldErrors'],
      });
    }
    return response;
  },
  (error) => {
    const errData = error.response?.data?.error as Record<string, unknown> | undefined;
    const code = errData?.['code'] ?? `HTTP-${error.response?.status ?? 0}`;
    const message =
      (errData?.['message'] as string) ??
      (error.response?.data?.message as string) ??
      error.message ??
      'Network error';
    const traceId = (error.response?.headers?.['x-trace-id'] as string) ?? undefined;
    const fieldErrors = errData?.['fieldErrors'];
    return Promise.reject({ code, message, traceId, fieldErrors });
  }
);

export const configRestoreApi = {
  /**
   * POST /api/eraf/admin/config/restore-baseline
   * 임원 데모로 변경된 메뉴·공통코드를 표준 상태로 되돌림. ROLE_ADMIN 전용.
   */
  restoreBaseline: (): Promise<RestoreBaselineResult> =>
    apiClient
      .post<unknown, { data: RestoreBaselineResult }>('/admin/config/restore-baseline')
      .then((res) => res.data),
};
